import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Tooltip,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { AddBox as AddIcon } from "@material-ui/icons";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";
import { useAuth } from "../../../../contexts/AuthContext";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { REGEX_DECIMAL } from "../../../../core/utils/regex";
import { cancelButton, saveButton } from "../../../../core/styles/ui.constants";
import { GENERAL } from "../../../../core/utils/roles.constants";
import SelectorGenerico from "../../../../components/Selector/SelectorGenerico";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import BotonGenerico from "../../../../components/Button/Button";

const MAX_DESCRIPCION_LENGTH = 255;

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "1200px",
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  editButton: {
    position: "absolute",
    top: theme.spacing(6),
    right: theme.spacing(6),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formRowItem: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(2),
  },
  field: {
    flex: 1,
  },
  descripcionContainer: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  button: {
    width: "30%",
  },
  actionButton: saveButton,
  cancelButton: cancelButton,
  autocompleteInputRoot: {
    "& .MuiOutlinedInput-root": {
      minHeight: 56, // Ajustá este valor a lo que necesites
    },
  },
}));

const IngresarItemsModal = forwardRef(
  ({ open, onClose, onSabeItem, sectorSelected, initialItem = null, readOnly = false, canEnableEdit = false }, ref) => {
    const classes = useStyles();

    const { getUser } = useAuth();
    const user = getUser();
    const esUsuarioGeneral = Number(user?.es_admin) === GENERAL;

    const [showContent, setShowContent] = useState(false);
    const [detailEditEnabled, setDetailEditEnabled] = useState(false);
    const isReadOnly = readOnly && !detailEditEnabled;

    // { selectedItem: "", cantidad: "", importeUnitario: "", selectedOs: "" },
    // const [informacionOSDefault, setInformacionOSDefault] = useState([
    //   { itemName: "", cantidad: "", importeUnitario: "", selectedOs: "" },
    // ]);
    const informacionOSDefault = [
      { itemName: "", cantidad: "", importeUnitario: "", selectedOs: "" },
    ];
    const [informacionOS, setInformacionOS] = useState(informacionOSDefault);

    const [selectedValue, setSelectedValue] = useState({
      selectedRubro: "",
      // selectedItem: "",
      selectedOs: "",
    });

    const [selectedSearch, setSelectedSearch] = useState({
      selectedRubro: "",
      // selectedItem: "",
      selectedOs: "",
    });

    const [openGeneric, setOpenGeneric] = useState({
      selectedRubro: false,
      // selectedItem: false,
      selectedOs: false,
    });

    // const [formDefaultValues, setFormDefaultValues] = useState({
    //   selectedOs: [""],
    //   selectedRubro: 0,
    //   descripcion: "",
    // });
    const formDefaultValues = {
      selectedOs: [""],
      selectedRubro: 0,
      descripcion: ""
    }

    const [formData, setFormData] = useState(formDefaultValues);
    const [errors, setErrors] = useState({});

    const [rubro, setRubros] = useState([]);
    // const [rubroOriginal, setRubroOriginal] = useState([]);
    const [os, setOs] = useState([]);
    // const [items, setItems] = useState([]);
    const [itemsOS, setItemsOS] = useState([]);
    // const [itemsOriginal, setItemsOriginal] = useState([]); // [{ IdItems, nombre, cantidad, precioDefault, ... }]

    const [anchorEl, setAnchorEl] = useState(null);
    const selectRef = useRef(null); // Ref para el contenedor del Select
    const selectedItemRef = useRef(null); // Ref para el contenedor del Select

    const [openOsIndex, setOpenOsIndex] = useState(null);
    const [anchorOsEl, setAnchorOsEl] = useState(null);

    // const [openItemIndex, setOpenItemIndex] = useState(null);
    const [anchorItemEl, setAnchorItemEl] = useState(null);
    const [searchOsPorIndex, setSearchOsPorIndex] = useState({});

    const handleCloseOs = () => {
      setOpenOsIndex(null);
      setAnchorOsEl(null);
    };

    const handleCloseItem = () => {
      // setOpenItemIndex(null);
      setAnchorItemEl(null);
    };

    const handleGenericSelectorConDependencias = (
      index,
      key,
      itemsOriginal = [],
      dependencias = []
    ) => (event, newValue) => {
      const idSeleccionado = newValue?.value ?? "";

      if (typeof index === "number") {
        // Si se usa en un array como informacionOS
        handleItemChange(index, key, idSeleccionado);
      } else {
        // Si se usa en un estado simple (selectedValue, selectedSearch)
        setSelectedValue((prev) => ({ ...prev, [key]: idSeleccionado }));
      }

      // Asignar valores derivados
      const objetoCompleto = itemsOriginal.find(
        (it) =>
          String(it.IdItems ?? it.IdRubro ?? it.IdOrdenServicio) ===
          String(idSeleccionado)
      );
      dependencias.forEach(({ campo, valorPorDefecto, campoFuente }) => {
        if (typeof index === "number") {
          handleItemChange(
            index,
            campo,
            objetoCompleto?.[campoFuente] ?? valorPorDefecto
          );
        } else {
          setSelectedValue((prev) => ({
            ...prev,
            [campo]: objetoCompleto?.[campoFuente] ?? valorPorDefecto,
          }));
        }
      });

      setSelectedSearch((prev) => ({ ...prev, [key]: newValue?.label ?? "" }));
      setOpenGeneric((prev) => ({ ...prev, [key]: false }));
    };

    const handleSearchChange = useCallback(
      (key) => (event, newInputValue, reason) => {
        if (reason === "input") {
          setSelectedSearch((prev) => ({
            ...prev,
            [key]: newInputValue,
          }));
        }
      },
      []
    );

    // Helpers para abrir/cerrar
    const handleOpenSearch = (key) => () =>
      setOpenGeneric((prev) => ({ ...prev, [key]: true }));

    const handleCloseSearch = (key) => () =>
      setOpenGeneric((prev) => ({ ...prev, [key]: false }));

    const close = () => {
      setFormData(formDefaultValues);
      setInformacionOS(informacionOSDefault);
      setSelectedValue({ selectedRubro: "", selectedOs: "" });
      setSelectedSearch({ selectedRubro: "", selectedOs: "" });
      setSearchOsPorIndex({});
      setDetailEditEnabled(false);
      setErrors({});
      onClose();
    };

    useEffect(() => {
      if (open) {
        setDetailEditEnabled(false);
      }
    }, [open, initialItem, readOnly]);

    // Exponer la función para resetear los campos
    useImperativeHandle(ref, () => ({
      resetFields: () => {
        setFormData(formDefaultValues);
        setErrors({});
      },
    }));

    useEffect(() => {
      if (showContent) {
        const fetchData = async () => {
          const [
            rubroResponse,
            osResponse,
            // itemsResponse,
            itemsOSResponse,
          ] = await Promise.all([
            api.rubros.getAll(),
            api.ordenesServicios.getAll(),
            // api.items.getAll(),
            api.itemsOS.getAll(),
          ]);

          const rubroOptions = rubroResponse.map((rubro) => ({
            value: String(rubro.IdRubro),
            label: `${rubro.Nombre || "Sin Nombre"}`,
          }));

          setRubros(rubroOptions);
          // setRubroOriginal(rubroResponse);

          // Mapear ítems a opciones para el selector
          // const itemOptions = itemsResponse.map((item) => ({
          //   value: String(item.IdItems),
          //   label: `${item.codigoItem} - ${item.nombre || "Sin nombre"}`,
          // }));
          // setItems(itemOptions); // `items` se usa como options en SelectorGenerico
          // setItemsOriginal(itemsResponse); // ítems completos

          const osFiltradas = osResponse.filter(
            (os) =>
              os.idSector === Number(sectorSelected) &&
              Number(os.oculto || 0) === 0
          );

          const osOptions = osFiltradas.map((os) => ({
            value: String(os.idOS),
            label: `${os.idOS}`,
          }));

          setOs(osOptions);

          setItemsOS(itemsOSResponse);
        };

        fetchData();
      }
    }, [showContent]);

    const handleClose = () => {
      setAnchorEl(null);
      // setSearchTerm('');
    };

    const handleClickOutside = (event) => {
      // Verifica si el clic ocurrió dentro del select o el input de búsqueda
      if (
        (selectRef.current && selectRef.current.contains(event.target)) ||
        (selectedItemRef.current &&
          selectedItemRef.current.contains(event.target)) ||
        event.target.id === "search-input-os" ||
        event.target.id === "search-input-item"
      ) {
        return; // No cerrar el menú si se clickea en los Selects o en los inputs de búsqueda
      }

      // Si el clic fue fuera del menú, cierra el menú
      if (anchorEl && !anchorEl.contains(event.target)) {
        handleClose();
      }
      if (anchorItemEl && !anchorItemEl.contains(event.target)) {
        handleCloseItem();
      }

      if (anchorOsEl && !anchorOsEl.contains(event.target)) {
        handleCloseOs();
      }
    };

    useEffect(() => {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, [anchorEl, anchorOsEl, anchorItemEl]);

    const validarDatos = () => {
      const newErrors = {};

      informacionOS.forEach((item, index) => {
        // if (!item.selectedItem)
        //   newErrors[`selectedItem_${index}`] = "Debe seleccionar un item.";
        if (!item.itemName)
          newErrors[`itemName_${index}`] = "Debe ingresar un item.";
        if (!esUsuarioGeneral) {
          if (!item.cantidad || Number(item.cantidad) <= 0)
            newErrors[`cantidad_${index}`] = "Cantidad inválida.";
          if (!item.importeUnitario)
            newErrors[`importeUnitario_${index}`] =
              "Debe ingresar un importe válido.";
        } else {
          if (item.cantidad !== "" && Number(item.cantidad) <= 0)
            newErrors[`cantidad_${index}`] = "Cantidad inválida.";
          if (item.importeUnitario !== "" && Number(item.importeUnitario) < 0)
            newErrors[`importeUnitario_${index}`] =
              "Debe ingresar un importe válido.";
        }
        // if (!item.selectedOs && user?.es_admin !== 3) // Solo si el usuario es general puede crear el item sin OS
        // if (!item.selectedOs)
        //   newErrors[`selectedOs_${index}`] =
        //     "Debe seleccionar una orden de servicio.";
      });
      if (!formData.selectedRubro)
        newErrors.selectedRubro = "Debe seleccionar un rubro.";
      if (formData.descripcion.length > MAX_DESCRIPCION_LENGTH) {
        newErrors.descripcion = `La descripción no puede superar los ${MAX_DESCRIPCION_LENGTH} caracteres.`;
      }

      setErrors(newErrors);

      return Object.keys(newErrors).length === 0;
    };

    const guardar = () => {
      const validacion = validarDatos();
      if (!validacion) return;

      try {
        const rubroSeleccionado = rubro.find(
          (r) => r.value === String(formData.selectedRubro)
        );

        const nuevoItem = {
          idRubro: formData.selectedRubro,
          nombreRubro: rubroSeleccionado
            ? rubroSeleccionado.label
            : "Sin Nombre",
          descripcion: formData.descripcion,
          informacionOS: informacionOS,
        };

        onSabeItem(nuevoItem);
        setInformacionOS(informacionOSDefault);
        close();
      } catch (error) {
        console.error("Error al guardar el item:", error.message);
      }
    };

    // const handleChange = (field) => (e) => {
    //   setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // };

    const calcularSaldoTotalPorOs = useCallback(
      (idOs) => {
        if (!idOs) return "";

        const itemsDeLaOS = itemsOS.filter(
          (item) => String(item.idOrdenServicio) === String(idOs)
        );

        const total = itemsDeLaOS.reduce((acc, current) => {
          const cantidad = parseFloat(current.cantidad) || 0;
          const precio = parseFloat(current.precioUnitario) || 0;
          return acc + cantidad * precio;
        }, 0);

        return total.toFixed(2);
      },
      [itemsOS]
    );

    const handleAddItem = () => {
      // { selectedOs: "", selectedItem: "", saldo: "", cantidad: "", cantidadMaxima: 0 },
      setInformacionOS([
        { itemName: "", cantidad: "", importeUnitario: "", selectedOs: "" },
        ...informacionOS,
      ]);

      setSearchOsPorIndex((prev) => {
        const next = { 0: "" };
        Object.keys(prev).forEach((key) => {
          const numericKey = Number(key);
          next[numericKey + 1] = prev[key];
        });
        return next;
      });
    };

    const handleItemChange = (index, field, value) => {
      const newItems = [...informacionOS];
      newItems[index][field] = value;

      // Verifica si ya se seleccionaron el OS y el item
      // const selectedOs =
      //   field === "selectedOs" ? value : newItems[index].selectedOs;
      // const selectedItem =
      //   field === "selectedItem" ? value : newItems[index].selectedItem;

      // if (selectedOs && selectedItem) {
      //   const match = itemsOS.find((item) =>
      //     item.idItems === Number(selectedItem) &&
      //     item.idOrdenServicio === Number(selectedOs)
      //   );
      //   if (match) {
      //     newItems[index].idItemsOS = match.idItemsOS;
      //   } else {
      //     // Si no hay coincidencia, asegúrate de limpiar idItemsOS si ya existía
      //     delete newItems[index].idItemsOS;
      //   }
      // }

      setInformacionOS(newItems);
    };

    const handleRemoveItem = (index) => {
      const newItems = informacionOS.filter((_, i) => i !== index);
      setInformacionOS(newItems);

      setSearchOsPorIndex((prev) => {
        const next = {};
        Object.keys(prev).forEach((key) => {
          const numericKey = Number(key);
          if (numericKey < index) {
            next[numericKey] = prev[key];
          } else if (numericKey > index) {
            next[numericKey - 1] = prev[key];
          }
        });
        return next;
      });

      if (openOsIndex === index) {
        setOpenOsIndex(null);
      } else if (typeof openOsIndex === "number" && openOsIndex > index) {
        setOpenOsIndex(openOsIndex - 1);
      }
    };

    useEffect(() => {
      if (open) {
        const timeout = setTimeout(() => setShowContent(true), 50);
        return () => clearTimeout(timeout);
      } else {
        setShowContent(false);
      }
    }, [open]);

    useEffect(() => {
      if (!open || !showContent) return;

      if (initialItem) {
        const rubroId = String(initialItem.idRubro || "");
        const rubroMatch = rubro.find((r) => String(r.value) === rubroId);

        setFormData((prev) => ({
          ...prev,
          selectedRubro: rubroId,
          descripcion: initialItem.descripcion || "",
        }));

        setSelectedValue((prev) => ({
          ...prev,
          selectedRubro: rubroId,
        }));

        setSelectedSearch((prev) => ({
          ...prev,
          selectedRubro: rubroMatch?.label || initialItem.nombreRubro || "",
        }));

        const osSearchMap = {};
        (initialItem.informacionOS || []).forEach((info, idx) => {
          osSearchMap[idx] = info?.selectedOs ? String(info.selectedOs) : "";
        });
        setSearchOsPorIndex(osSearchMap);

        const infoCargada = Array.isArray(initialItem.informacionOS)
          ? initialItem.informacionOS.map((info) => ({
            itemName: info.itemName || "",
            cantidad: String(info.cantidad ?? ""),
            importeUnitario: String(info.importeUnitario ?? ""),
            selectedOs: String(info.selectedOs ?? ""),
            importeTotal:
              info.importeTotal ??
              ((Number(info.cantidad) || 0) * (Number(info.importeUnitario) || 0)).toFixed(2),
            saldoTotal:
              info.saldoTotal ||
              calcularSaldoTotalPorOs(info.selectedOs),
          }))
          : informacionOSDefault;

        setInformacionOS(infoCargada.length > 0 ? infoCargada : informacionOSDefault);
      } else {
        setFormData(formDefaultValues);
        setInformacionOS(informacionOSDefault);
        setSelectedValue({ selectedRubro: "", selectedOs: "" });
        setSelectedSearch({ selectedRubro: "", selectedOs: "" });
        setSearchOsPorIndex({});
      }
    }, [open, showContent, initialItem, rubro, calcularSaldoTotalPorOs]);

    return (
      <>
        <Dialog open={open} onClose={close} maxWidth="xl" fullScreen>
          <DialogTitle disableTypography>
            <Typography variant="h6" className={classes.titleText}>
              {isReadOnly ? "Detalle del ítem" : "Ingresar Items"}
            </Typography>
            {readOnly && canEnableEdit && (
              <Tooltip
                title={
                  <span style={{ fontSize: "0.8rem" }}>
                    {detailEditEnabled ? "Cancelar edición" : "Habilitar edición"}
                  </span>
                }
              >
                <IconButton
                  aria-label="toggle-edit"
                  className={classes.editButton}
                  onClick={() => setDetailEditEnabled((prev) => !prev)}
                >
                  {detailEditEnabled ? <CloseIcon /> : <EditIcon />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={close}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            {showContent && (
              <>
                {/* Primera fila */}
                <div className={classes.field}>
                  <div className={classes.field}>
                    <SelectorGenerico
                      autoCompleteStyle={classes.autocompleteInputRoot}
                      label="Rubro"
                      classNameDiv={classes.formRowItem}
                      value={selectedValue.selectedRubro || ""}
                      disabled={isReadOnly}
                      // onChange={handleRubroChange}
                      onChange={(event, newValue) => {
                        const nuevoIdRubro = newValue?.value;

                        // if (informacionOS.length > 0) {
                        // if (informacionOS.some((item) => item.selectedItem)) {
                        //   const confirmar = window.confirm(
                        //     "Ya hay ítems cargados. Cambiar el rubro vaciará los ítems actuales. ¿Desea continuar?"
                        //   );

                        //   if (!confirmar) return;

                        //   // Vaciar ítems
                        //   setInformacionOS(informacionOSDefault);

                        //   // Filtrar ítems según el rubro seleccionado
                        //   const itemsFiltrados = itemsOriginal.filter(
                        //     (itemO) =>
                        //       String(itemO.idRubro) === String(nuevoIdRubro)
                        //   );

                        //   const itemOptions = itemsFiltrados.map((item) => ({
                        //     value: String(item.IdItems), // ⚠️ convertir a string para que coincida con `value` de SelectorGenerico
                        //     label: `${item.codigoItem} - ${
                        //       item.nombre || "Sin nombre"
                        //     }`,
                        //   }));
                        //   setItems(itemOptions);

                        //   setSelectedValue({
                        //     ...selectedValue,
                        //     selectedItem: "",
                        //     selectedOs: "",
                        //   });
                        //   setSelectedSearch({
                        //     ...selectedSearch,
                        //     selectedItem: "",
                        //     selectedOs: "",
                        //   });
                        // } else {
                        //   // Filtrar ítems según el rubro seleccionado
                        //   const itemsFiltrados = itemsOriginal.filter(
                        //     (itemO) =>
                        //       String(itemO.idRubro) === String(nuevoIdRubro)
                        //   );

                        //   const itemOptions = itemsFiltrados.map((item) => ({
                        //     value: String(item.IdItems), // ⚠️ convertir a string para que coincida con `value` de SelectorGenerico
                        //     label: `${item.codigoItem} - ${
                        //       item.nombre || "Sin nombre"
                        //     }`,
                        //   }));

                        //   setItems(itemOptions);
                        // }

                        setFormData({
                          ...formData,
                          selectedRubro: nuevoIdRubro,
                        });

                        // Ejecutar lógica base (actualiza selectedValue y otras dependencias)
                        handleGenericSelectorConDependencias(
                          null,
                          "selectedRubro"
                        )(event, newValue);
                      }}
                      inputValue={selectedSearch.selectedRubro}
                      // newInputValue={selectedSearch.rubro}
                      onInputChange={handleSearchChange("selectedRubro")}
                      onHighlightChange={(event, newHighlight) => {
                        // setHighlightedIndex(newHighlight);
                      }}
                      options={rubro}
                      open={openGeneric.selectedRubro}
                      onOpen={handleOpenSearch("selectedRubro")}
                      onClose={handleCloseSearch("selectedRubro")}
                      error={!!errors.selectedRubro}
                    />
                  </div>
                </div>
                <div className={classes.formRow}>
                  <div
                    className={classes.field}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <br />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        style={{ marginRight: "1rem" }}
                      >
                        Ordenes de servicio
                      </Typography>
                      {!isReadOnly && (
                        <Tooltip
                          title={
                            <span style={{ fontSize: "0.8rem" }}>
                              Agregar otro grupo de OS/Item/Saldo/Cantidad
                            </span>
                          }
                          arrow
                        >
                          <IconButton
                            color="primary"
                            onClick={handleAddItem}
                            size="small"
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <br />

                    {informacionOS.map((item, index) => (
                      <div key={index} className={classes.formRow}>
                        {/* Selecionar item */}
                        <div
                          className={classes.field}
                          style={{ maxWidth: "12rem" }}
                        >
                          {/* <SelectorGenerico
                            autoCompleteStyle={classes.autocompleteInputRoot}
                            label="ITEM"
                            tieneTooltip
                            titleTooltip="Código del item - Nombre del item."
                            classNameDiv={classes.formRowItem}
                            value={item.selectedItem || ""}
                            onChange={handleGenericSelectorConDependencias(
                              index,
                              "selectedItem",
                              itemsOriginal,
                              [
                                {
                                  campo: "cantidad",
                                  valorPorDefecto: 0,
                                  campoFuente: "cantidad",
                                },
                                {
                                  campo: "importeUnitario",
                                  valorPorDefecto: 0,
                                  campoFuente: "precioDefault",
                                },
                                {
                                  campo: "importeTotal",
                                  valorPorDefecto: 0,
                                  campoFuente: "cantidad * importeUnitario",
                                },
                              ]
                            )}
                            inputValue={selectedSearch.selectedItem}
                            onInputChange={handleSearchChange("selectedItem")}
                            onHighlightChange={(event, newHighlight) => {
                              // setHighlightedIndex(newHighlight);
                            }}
                            options={items}
                            // plainObject={items}
                            open={openGeneric.selectedItem}
                            onOpen={handleOpenSearch("selectedItem")}
                            onClose={handleCloseSearch("selectedItem")}
                            error={!!errors[`selectedItem_${index}`]}
                          /> */}
                          <Typography
                            variant="subtitle1"
                            style={{ marginBottom: 4 }}
                          >
                            Item
                          </Typography>
                          <TextField
                            value={item.itemName || ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              handleItemChange(index, "itemName", inputValue);
                            }}
                            variant="outlined"
                            type="text"
                            fullWidth
                            disabled={isReadOnly}
                            error={!!errors[`itemName_${index}`]}
                            helperText={errors[`itemName_${index}`]}
                          />
                          {errors[`selectedItem_${index}`] && (
                            <Typography color="error" variant="caption">
                              {errors[`selectedItem_${index}`]}
                            </Typography>
                          )}
                        </div>

                        {/* Ingresar cantidad */}
                        <div
                          className={classes.field}
                          style={{ maxWidth: "10rem" }}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ marginBottom: 4 }}
                          >
                            Cantidad
                          </Typography>
                          <TextField
                            value={item.cantidad || ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;

                              if (
                                inputValue === "" ||
                                REGEX_DECIMAL.test(inputValue)
                              ) {
                                // if (Number(inputValue) <= 0) {
                                //   handleItemChange(index, "cantidad", 0);
                                //   return;
                                // }

                                handleItemChange(index, "cantidad", inputValue);
                                handleItemChange(
                                  index,
                                  "importeTotal",
                                  (
                                    item.cantidad * item.importeUnitario
                                  ).toFixed(2)
                                );
                              }
                            }}
                            variant="outlined"
                            type="number"
                            fullWidth
                            disabled={isReadOnly}
                            error={!!errors[`cantidad_${index}`]}
                            helperText={errors[`cantidad_${index}`]}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-", "."].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>

                        {/* Ingresar importe unitario */}
                        <div
                          className={classes.field}
                          style={{ maxWidth: "12rem" }}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ marginBottom: 4 }}
                          >
                            {" "}
                            Importe unitario
                          </Typography>
                          <TextField
                            value={item.importeUnitario || ""}
                            onChange={(e) => {
                              const inputValue = e.target.value;

                              if (
                                inputValue === "" ||
                                REGEX_DECIMAL.test(inputValue)
                              ) {
                                handleItemChange(
                                  index,
                                  "importeUnitario",
                                  inputValue
                                );

                                // Actualiza el importe total si hay cantidad
                                const cantidadNum =
                                  parseFloat(item.cantidad) || 0;
                                const unitarioNum = parseFloat(inputValue) || 0;
                                handleItemChange(
                                  index,
                                  "importeTotal",
                                  (cantidadNum * unitarioNum).toFixed(2)
                                );
                              }
                            }}
                            variant="outlined"
                            type="number"
                            fullWidth
                            disabled={isReadOnly}
                            error={!!errors[`importeUnitario_${index}`]}
                            helperText={errors[`importeUnitario_${index}`]}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-", "."].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>

                        {/* <div className={classes.field}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                          <Typography variant="subtitle1">Saldo </Typography>
                          <Tooltip title="Es el valor unitario del item">
                            <InfoIcon fontSize="small" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                          </Tooltip>
                        </div>
                        <TextField
                          value={item.saldo}
                          onChange={(e) => handleItemChange(index, "saldo", e.target.value)}
                          variant="outlined"
                          fullWidth
                          type="number"
                          disabled
                        />
                      </div> */}

                        {/* Valor del importe total de los item */}
                        <div
                          className={classes.field}
                          style={{ maxWidth: "12rem" }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 4,
                            }}
                          >
                            <Typography variant="subtitle1">
                              Importe total
                            </Typography>
                            {/* <Tooltip title="Es la multiplicación de la cantidad por el importe unitario."> */}
                            <Tooltip
                              title={
                                <span style={{ fontSize: "0.8rem" }}>
                                  Es la multiplicación de la cantidad por el
                                  importe unitario.
                                </span>
                              }
                            >
                              <InfoIcon
                                fontSize="small"
                                style={{ color: "rgba(0, 0, 0, 0.54)" }}
                              />
                            </Tooltip>
                          </div>
                          <TextField
                            value={item.importeTotal || ""}
                            onChange={(e) => {
                              handleItemChange(
                                index,
                                "importeTotal",
                                e.target.value
                              );
                            }}
                            variant="outlined"
                            type="number"
                            fullWidth
                            error={!!errors[`importeTotal_${index}`]}
                            helperText={errors[`importeTotal_${index}`]}
                            disabled
                          />
                        </div>

                        <div
                          className={classes.field}
                          style={{ maxWidth: "12rem" }}
                        >
                          {isReadOnly ? (
                            <>
                              <Typography
                                variant="subtitle1"
                                style={{ marginBottom: 4 }}
                              >
                                OS A IMPUTAR
                              </Typography>
                              <TextField
                                variant="outlined"
                                fullWidth
                                value={item.selectedOs || ""}
                                disabled
                              />
                            </>
                          ) : (
                            <>
                              {/* Seleccionar OS */}
                              <SelectorGenerico
                                autoCompleteStyle={classes.autocompleteInputRoot}
                                label="OS A IMPUTAR"
                                tieneTooltip
                                titleTooltip="Solo se muestran las órdenes de servicios, según el sector que se ingreso en la requisición."
                                classNameDiv={classes.formRowItem}
                                value={item.selectedOs || ""}
                                onChange={(event, newValue) => {
                                  const idOSSeleccionada = newValue?.value;

                                  handleItemChange(
                                    index,
                                    "selectedOs",
                                    idOSSeleccionada ?? ""
                                  );

                                  setSearchOsPorIndex((prev) => ({
                                    ...prev,
                                    [index]: newValue?.label || "",
                                  }));

                                  handleItemChange(
                                    index,
                                    "saldoTotal",
                                    calcularSaldoTotalPorOs(idOSSeleccionada)
                                  );
                                }}
                                inputValue={searchOsPorIndex[index] ?? ""}
                                onInputChange={(event, newInputValue, reason) => {
                                  if (reason === "input") {
                                    setSearchOsPorIndex((prev) => ({
                                      ...prev,
                                      [index]: newInputValue,
                                    }));
                                  }
                                }}
                                onHighlightChange={(event, newHighlight) => {
                                  // setHighlightedIndex(newHighlight);
                                }}
                                options={os}
                                open={openOsIndex === index}
                                onOpen={() => setOpenOsIndex(index)}
                                onClose={() => setOpenOsIndex(null)}
                                error={!!errors[`selectedOs_${index}`]}
                                disabled={user?.es_admin === GENERAL} // Si es usuario general, no selecciona OS.
                              />
                              {errors[`selectedOs_${index}`] && (
                                <Typography color="error" variant="caption">
                                  {errors[`selectedOs_${index}`]}
                                </Typography>
                              )}
                            </>
                          )}
                        </div>

                        {/* Salto total de la orden de servicio */}
                        <div className={classes.field}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 4,
                            }}
                          >
                            <Typography variant="subtitle1">
                              SALDO TOTAL
                            </Typography>
                            <Tooltip
                              title={
                                <span style={{ fontSize: "0.8rem" }}>
                                  Valor total de la orden de servicio.
                                </span>
                              }
                            >
                              <InfoIcon
                                fontSize="small"
                                style={{ color: "rgba(0, 0, 0, 0.54)" }}
                              />
                            </Tooltip>
                          </div>
                          <TextField
                            value={item.saldoTotal || calcularSaldoTotalPorOs(item.selectedOs) || ""}
                            onChange={(e) => {
                              handleItemChange(
                                index,
                                "saldoTotal",
                                e.target.value
                              );
                            }}
                            variant="outlined"
                            type="number"
                            fullWidth
                            error={!!errors[`saldoTotal_${index}`]}
                            helperText={errors[`saldoTotal_${index}`]}
                            disabled
                          />
                        </div>

                        {!isReadOnly && informacionOS.length > 1 && (
                          <IconButton
                            color="secondary"
                            onClick={() => handleRemoveItem(index)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Descripción */}
                <div className={classes.descripcionContainer}>
                  <Typography variant="subtitle1">Descripción</Typography>
                  <TextField
                    value={formData.descripcion}
                    // onChange={(e) => { handleChange("descripcion")(e); }}
                    onChange={(e, newValue) => {
                      const descripcion = e.target.value;
                      setFormData({ ...formData, descripcion });
                      setErrors((prev) => {
                        const nextErrors = { ...prev };
                        if (descripcion.length > MAX_DESCRIPCION_LENGTH) {
                          nextErrors.descripcion = `La descripción no puede superar los ${MAX_DESCRIPCION_LENGTH} caracteres.`;
                        } else {
                          delete nextErrors.descripcion;
                        }
                        return nextErrors;
                      });
                    }}
                    variant="outlined"
                    multiline
                    rows={4}
                    fullWidth
                    disabled={isReadOnly}
                    error={!!errors.descripcion}
                    helperText={
                      errors.descripcion ||
                      `${formData.descripcion.length}/${MAX_DESCRIPCION_LENGTH} caracteres`
                    }
                  />
                </div>
              </>
            )}
          </DialogContent>
          <DialogFooter>
            {!isReadOnly && (
              <BotonGenerico
                tipo="guardar"
                color="primary"
                onClick={guardar}
                text="Guardar"
              />
            )}
            <BotonGenerico
              tipo={isReadOnly ? "sinIconos" : "cancelar"}
              color="secondary"
              onClick={close}
              text={isReadOnly ? "Cerrar" : "Cancelar"}
            />
            {/* <Button
              variant="contained"
              color="primary"
              className={classes.actionButton}
              startIcon={<CheckIcon />}
              onClick={guardar}
            >
              Guardar
            </Button>
            <Button
              variant="outlined"
              className={classes.cancelButton}
              startIcon={<CloseIcon />}
              onClick={() => {
                close();
              }}
            >
              Cancelar
            </Button> */}
          </DialogFooter>
        </Dialog>

        {/* <ConfirmDialog
        open={openAlertSector}
        onClose={() => setOpenAlertSector(false)}
        onConfirm={() =>
          handleGenericUpdate({
            selectedItem: selectedRow,
            data: filteredEvents,
            setData: (newData) => {
              setFilteredEvents(newData);
              setDataEvents(newData);
            },
            apiUpdateFunction: api.requisiciones.update,
            idField: "idRequision",
            updatePayload: { requisionOculto: true },
            closeDialog: handleCloseDialog,
          })
        }
        title="Ocultar Registro"
        description="Los registros ocultados solo serán recuperados por el Administrador."
        confirmText="Ocultar registro"
        cancelText="Cancelar"
        icon={<ErrorOutlineIcon className={classes.errorIcon} />}
        classes={{
          dialogTitle: classes.dialogTitle,
          dialogContent: classes.dialogContent,
          dialogButtons: classes.dialogButtons,
          confirmButton: classes.greenButton,
          cancelButton: classes.greenButton,
          iconWrapper: classes.iconWrapper,
        }}
      /> */}
      </>
    );
  }
);

export default IngresarItemsModal;
